/**
 * Dialog đăng ký tài khoản mới.
 *
 * Validate số điện thoại Việt Nam trước khi gọi API register.
 */
import { useEffect, useState } from 'react';
import { ButtonSpinner } from './ButtonSpinner';
import { BrandMark } from './BrandMark';
import { ErrorMessage } from './ErrorMessage';
import { ActionDialog } from './ActionDialog';
import { PhoneInput } from './PhoneInput';
import { useAuth } from '../../hooks/useAuth';
import { getApiErrorMessage } from '../../shared/utils/apiErrors';
import { UI_MESSAGES } from '../../shared/constants/messages';
import { TEXT } from '../../shared/languages';
import {
  getPhoneValidationError,
  normalizeVietnamesePhone,
} from '../../shared/utils/vietnamesePhone';

/**
 * Modal đăng ký.
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} [props.onSuccess]
 * @param {Function} [props.onSwitchToLogin]
 */
export const RegisterDialog = ({ isOpen, onClose, onSuccess, onSwitchToLogin }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const t = TEXT.auth;

  useEffect(() => {
    if (!isOpen) {
      setForm({ name: '', email: '', phone: '', password: '' });
      setError('');
      setPhoneError('');
    }
  }, [isOpen]);

  const handleClose = () => {
    if (submitting) {
      return;
    }

    setError('');
    setPhoneError('');
    onClose();
  };

  const updateField = (field, value) => {
    if (field === 'phone') {
      setPhoneError('');
    }

    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setPhoneError('');

    const phoneValidationKey = getPhoneValidationError(form.phone);

    if (phoneValidationKey) {
      setPhoneError(TEXT.validation[phoneValidationKey]);
      setSubmitting(false);
      return;
    }

    try {
      await register(
        form.name,
        form.email,
        form.password,
        normalizeVietnamesePhone(form.phone)
      );
      onSuccess?.();
      onClose();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, UI_MESSAGES.registerError));
    } finally {
      setSubmitting(false);
    }
  };

  const switchToLogin = () => {
    onClose();
    onSwitchToLogin?.();
  };

  return (
    <ActionDialog
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabel={t.registerTitle}
      layout="airbnb"
      panelClassName="action-dialog-panel--auth action-dialog-panel--auth-register"
    >
      <div className="auth-dialog-head">
        <BrandMark className="brand-mark--compact" />
        <div className="auth-dialog-head-copy">
          <h2 className="auth-dialog-title">{t.registerTitle}</h2>
          <p className="auth-dialog-subtitle auth-dialog-subtitle--plain">{t.registerDescription}</p>
        </div>
      </div>

      <form className="auth-dialog-form" onSubmit={handleSubmit}>
        <ErrorMessage message={error} />
        <input
          className="auth-dialog-input"
          value={form.name}
          onChange={(event) => updateField('name', event.target.value)}
          placeholder={t.fullName}
          autoComplete="name"
          required
        />
        <input
          className="auth-dialog-input"
          type="email"
          value={form.email}
          onChange={(event) => updateField('email', event.target.value)}
          placeholder={t.emailPlaceholder}
          autoComplete="email"
          required
        />
        <div className="auth-dialog-field">
          <PhoneInput
            className="auth-dialog-input"
            value={form.phone}
            onChange={(value) => updateField('phone', value)}
            placeholder={t.phone}
            required
          />
          {phoneError && <span className="auth-dialog-field-hint">{phoneError}</span>}
        </div>
        <input
          className="auth-dialog-input"
          type="password"
          value={form.password}
          onChange={(event) => updateField('password', event.target.value)}
          placeholder={t.passwordPlaceholder}
          autoComplete="new-password"
          minLength={8}
          required
        />
        <button type="submit" className="auth-dialog-submit" disabled={submitting}>
          {submitting && <ButtonSpinner />}
          {submitting ? t.registerSubmitting : t.registerTitle}
        </button>
      </form>

      <p className="auth-dialog-footer-note">
        {t.hasAccount}{' '}
        <button type="button" className="auth-dialog-footer-link" onClick={switchToLogin}>
          {t.loginTitle}
        </button>
      </p>
    </ActionDialog>
  );
};
